/**
 * Public entry point for SDK telemetry.
 *
 * Called once from `SDKProvider` boot effect when the host app passes a
 * `telemetry` prop. Composes the OTel resource, OTLP/JSON exporter, and
 * translator into a single subscriber on the `sdkEvents` bus.
 *
 * Guards against double-emission:
 *
 *   1. `telemetryInstalled` WeakSet keyed by QueryClient — second install on
 *      the same client returns a no-op + console.warn.
 *   2. `isOtelTelemetryActive()` module-level boolean — read by the four
 *      legacy `installBetterStack*` subscribers so they auto-no-op when the
 *      new translator is live (prevents double-fire during migration).
 *   3. SSR guard — returns no-op when running outside web/RN (Server
 *      Components, Node SSR pre-render).
 *   4. HMR cleanup — pins the prior dispose closure on `globalThis` so a
 *      module replacement calls it before installing a fresh translator.
 *   5. `config.enabled === false` — explicit opt-out, returns no-op.
 *
 * Once these five guards are passed, telemetry installs exactly once per
 * provider lifetime.
 */

import type { QueryClient } from '@tanstack/react-query';
import { createOtlpJsonExporter, type OtlpJsonExporter } from './otlp-json-exporter.js';
import { warnIfDevtoolFlagOnInProd } from './pii-scrub.js';
import { buildResource, type ResourceConfig } from './resource.js';
import { installTranslator, type TranslatorConfig } from './translator.js';

/** WeakSet — referenced by legacy installers for auto-no-op. */
export const telemetryInstalled = new WeakSet<QueryClient>();

/** Module-level flag — referenced by legacy installers that have no `qc` param. */
let _anyInstalled = false;

/** Read-only accessor for legacy installers. */
export function isOtelTelemetryActive(): boolean {
  return _anyInstalled;
}

const HMR_KEY = '__morfi_telemetryDispose';

export interface TelemetryConfig {
  readonly otlp: {
    readonly host: string;
    readonly token: string;
    readonly gzip?: boolean;
  };
  readonly app: {
    readonly name: string;
    readonly version: string;
    readonly build?: string;
  };
  readonly network: 'devnet' | 'mainnet';
  readonly sdkVersion?: string;
  /** Optional override of auto-detected platform/os/runtime. */
  readonly platform?: ResourceConfig['platform'];
  /** Per-emit attribute enricher (user.id, session.id, app.route). */
  readonly enrich?: TranslatorConfig['enrich'];
  /** Trace sampling rate, 0.0-1.0. Default 1.0. */
  readonly traceSampleRate?: number;
  /** Explicit opt-out — useful in dev/CI. */
  readonly enabled?: boolean;
  /** Optional asynchronous transport validation that must pass before install. */
  readonly beforeStart?: () => Promise<boolean>;
  /**
   * Optional platform-adapter `appState` slot. When provided, the
   * exporter flushes on `'hidden'` / `'freeze'` lifecycle events
   * (web pagehide, RN AppState background). Pass `adapter.appState`
   * from `<SDKProvider>`'s adapter. Without it the exporter still
   * works — there's no auto-flush on lifecycle, only on the
   * exporter's internal cadence.
   */
  readonly appState?: import('../platform/adapter.js').AppStateSlot;
}

/**
 * Install the OTel telemetry pipeline. Returns a dispose closure the caller
 * MUST invoke on unmount (the boot effect adds it to the teardown list).
 *
 * Idempotent: a second call on the same QueryClient returns a no-op.
 */
export function installOtelTelemetry(qc: QueryClient, config: TelemetryConfig): () => void {
  // Guard 5 — explicit opt-out.
  if (config.enabled === false) return noop;
  if (config.beforeStart !== undefined) {
    let disposed = false;
    let disposeActive = noop;
    let validation: Promise<boolean>;
    try {
      validation = config.beforeStart();
    } catch {
      return noop;
    }
    void validation
      .then((ready) => {
        if (!ready || disposed) return;
        disposeActive = installReadyOtelTelemetry(qc, config);
      })
      .catch(noop);
    return (): void => {
      disposed = true;
      disposeActive();
    };
  }
  return installReadyOtelTelemetry(qc, config);
}

function installReadyOtelTelemetry(qc: QueryClient, config: TelemetryConfig): () => void {
  // Guard 3 — SSR. We accept either a real browser (`window` defined) or RN
  // (`navigator.product === 'ReactNative'`). Server Components / Node SSR
  // run with neither — no telemetry, no exporter, no overhead.
  if (typeof window === 'undefined') {
    const nav = (globalThis as { navigator?: { product?: string } }).navigator;
    if (nav?.product !== 'ReactNative') return noop;
  }

  // Guard 1 — per-QueryClient install guard.
  if (telemetryInstalled.has(qc)) {
    console.warn(
      '[morfi-sdk:telemetry] installOtelTelemetry called twice with the same QueryClient — second call is a no-op.',
    );
    return noop;
  }

  // Guard 4 — HMR cleanup.
  const g = globalThis as Record<string, unknown>;
  const prior = g[HMR_KEY];
  if (typeof prior === 'function') {
    try {
      (prior as () => void)();
    } catch {
      /* never throw from HMR teardown */
    }
  }

  telemetryInstalled.add(qc);
  _anyInstalled = true;

  // Operator tripwire — warn if devtool flag is on in a prod build.
  warnIfDevtoolFlagOnInProd();

  const resource = buildResource({
    app: config.app,
    network: config.network,
    sdkVersion: config.sdkVersion ?? '2.0.0',
    ...(config.platform !== undefined ? { platform: config.platform } : {}),
  });

  const exporter: OtlpJsonExporter = createOtlpJsonExporter({
    host: config.otlp.host,
    token: config.otlp.token,
    gzip: config.otlp.gzip ?? true,
  });

  const offTranslator = installTranslator(exporter, resource, {
    ...(config.enrich !== undefined ? { enrich: config.enrich } : {}),
    ...(config.traceSampleRate !== undefined ? { traceSampleRate: config.traceSampleRate } : {}),
  });

  // Flush on page-lifecycle hooks. The platform adapter's appState
  // listener delivers `'hidden'` (web visibilitychange) + `'freeze'`
  // (Chromium bfcache) + RN AppState background as a unified surface.
  // The exporter flushes on any of these — `pagehide` was the legacy
  // direct binding; the adapter covers the same signal cross-platform.
  let offPageHide: (() => void) | null = null;
  if (config.appState !== undefined) {
    offPageHide = config.appState.addListener((ev) => {
      if (ev.kind === 'hidden' || ev.kind === 'freeze') {
        void exporter.flush();
      }
    });
  }

  const dispose = (): void => {
    try {
      offTranslator();
    } catch {
      /* swallow */
    }
    offPageHide?.();
    // Best-effort flush + shutdown. Don't await — teardown is sync.
    void exporter.shutdown();
    telemetryInstalled.delete(qc);
    // WeakSet has no .size — we conservatively reset the flag on every
    // dispose. A second concurrent provider's install will flip it back
    // to true on its next install call (the only path that writes it).
    _anyInstalled = false;
    delete g[HMR_KEY];
  };
  g[HMR_KEY] = dispose;

  return dispose;
}

function noop(): void {
  /* no-op for guarded paths */
}
